import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getMsg } from "../../utils/antdStatic";
import { useSidebar } from "../../hooks/useSidebar";
import api from "../../services/api";
import { apiFetch } from "../../services/apiClient";
import { mapPropiedadCalendario } from "./propiedadMapper";
import { useAuth } from "../../context/AuthContext";
import { useWebSocket } from "../../context/WebSocketContext";
import { isAdminRole, normalizeRole } from "../../utils/roles.utils";
import { fuzzyIncludes } from "../../utils/fuzzyMatch";
import { ahoraChile } from "../../utils/fechaChile";
import { useFiltroPersistido } from "../../hooks/useFiltroPersistido";
import { CADUCIDAD_FECHAS_DIAS } from "../../hooks/filtrosPersistidos";
import { acotarRangoCalendario } from "../../utils/rangoCalendario";

/**
 * Nombres de TODO el equipo del aseo unidos ("Ana + Rosa"), o null si el feed
 * no trae `aseadores` (payload legacy) — el caller cae al nombre de la
 * responsable. Round 2026-08-25 (reparto multi-aseadora).
 */
function nombreEquipoAseo(c: any): string | null {
  const lista = Array.isArray(c?.aseadores) ? c.aseadores : [];
  const nombres = lista.map((x: any) => x?.nombre).filter(Boolean);
  return nombres.length > 0 ? nombres.join(" + ") : null;
}

export type CalendarEventItem = {
  kind: "cleaning" | "blocked" | "maintenance";
  id: number;
  propertyId: number;
  startISO: string; // ISO datetime
  endISO: string; // ISO datetime
  razon?: string;
  notas?: string;
  urgencia?: string;
  aseadorNombre?: string;
  createdByAdmin?: boolean;
  idUsuarioCreador?: number | null;
  /**
   * Huésped de la reserva padre (solo para aseos AUTO vinculados a un
   * booking cargado en el calendario). Permite renderizar
   * "Aseo de Pedro Pérez" en el tooltip de la barra y en el card del
   * detail modal. Round 2026-05-30.
   */
  huespedReserva?: string;
  /**
   * FK al booking padre — replicado en el item para que el tooltip
   * pueda mostrar el código corto/nombre sin tener que volver a buscar
   * en otra estructura. Round 2026-05-30.
   */
  reservaId?: number;
};

export type Property = {
  id: number;
  nombre: string;
  color?: string | null;
  costoAseo?: number | null;
  sueldoAseo?: number | null;
  tasaComisionAf?: number | null;
  idPropiedadCorto?: string | null;
  // Precio BASE mínimo por noche acordado con el propietario (lo que paga el
  // arrendatario). Si al fijar el precio base en el calendario se pone un valor
  // menor, el sistema pide confirmación. Null = sin mínimo. Ver MetaEditModal.
  precioMinimoNoche?: number | null;
  // «Mensaje inicial» de la sección «Información para el huésped»: lo copia el
  // botón/ítem «Copiar instrucciones». SOLO admin/superadmin (a los demás roles
  // el mapper lo deja en null); se lee con `obtenerMensajeInicial` (utils/mensajeInicial).
  mensajeInicial?: string | null;
  // Subitems de limpieza (desglose neto/total) — necesarios para calcular
  // Ingreso AF al crear un arriendo. Ver docs/modulo-arriendos-precios.md §Desglose de limpieza.
  transporteNeto?: number | null;
  transporteTotal?: number | null;
  materialesNeto?: number | null;
  materialesTotal?: number | null;
  lavanderiaNeto?: number | null;
  lavanderiaTotal?: number | null;
  hasLavanderiaAf?: boolean;
  // Geografía + cliente — para ordenar/filtrar y mostrar el subtexto bajo el
  // nombre en la grilla. Vienen de `/propiedades` (region/comuna/sector son IDs;
  // *Nombre los nombres resueltos; clienteNombre = client.displayName).
  regionId?: number | null;
  comunaId?: number | null;
  sectorId?: number | null;
  regionNombre?: string | null;
  comunaNombre?: string | null;
  sectorNombre?: string | null;
  clienteNombre?: string | null;
  /** Nombres de propietarios (resueltos de propietario_ids) — para la búsqueda. */
  propietarioNombres?: string[];
};

/** Criterio de orden de las filas de propiedad en el calendario. */
export type PropSortKey = "nombre" | "region" | "comuna" | "sector" | "cliente";
/** Filtros activos (por nombre) de las filas de propiedad. */
export type PropFilters = { region: string[]; comuna: string[]; sector: string[]; cliente: string[] };

export type Booking = {
  id: number;
  idPropiedad: number | null;
  ingreso: string | null; // YYYY-MM-DD
  salida: string | null; // YYYY-MM-DD
  noches: number | null;
  precioNochesClp: number | string | null;
  reservaTotalClp: number | null;
  nombrePlataforma: string | null;
  idPlataforma: string | null;
  nombreHuesped?: string | null;
  telefonoHuesped?: string | null;
  idReservaPlataforma?: string | null;
  /**
   * ID corto canónico de Stays (formato JS11J — `payload.id` del webhook).
   * Es el identificador que usan en la plataforma origen y el que aparece
   * en el panel `/stays-webhooks`. Lo usamos en tooltips/cards para que el
   * admin pueda cruzar la reserva con el webhook.
   */
  idReservaStays?: string | null;
  fechaCreacion?: string | null;
  estado?: string | null;
  inmutable?: boolean | null;
};

export type BookingDetail = Booking & {
  // requeridos por el form
  precioNocheClp: number; // required
  limpiezaClp: number; // required
  reservaTotalPropietario: number;
  // opcionales del form
  comisionPlataformaClp?: number | null;
  descuentoClp?: number | null;

  // listas (si las usas)
  extrasSinComisionClp?: { name: string; _f_val: number }[];
  extrasConComisionClp?: { name: string; _f_val: number }[];

  // pagos
  estadoPago?: "pendiente" | "pagado" | "parcial" | "reembolsado" | null;
  tipoPago?: "transferencia" | "efectivo" | "tarjeta" | "otro" | null;

  notaInterna?: string | null;

  // si quieres persistir horas (si no, el modal deriva desde thirds)
  startTime?: string | null; // "HH:mm"
  endTime?: string | null; // "HH:mm"
};

export type Cleaning = {
  id: number;

  idPropiedad: number | null;

  idAseador?: number | null;
  estado?: string | null;

  fecha: string | null;

  fechaFin?: string | null;

  horaInicio?: string | null;

  urgencia?: string | null;

  /** FK al booking padre si es aseo AUTO (`reserva_id` backend). */
  reservaId?: number | null;

  observaciones?: string | null;

  costoLimpieza?: number | null;
  sueldoAseador?: number | null;
  // Round 2026-05-11: bono/descuento per-aseo eliminados — viven en
  // `gastos` como movimientos extra del aseador.

  aseador?: {
    id: number;
    nombre?: string | null;
    telefono?: string | null;
    email?: string | null;
  } | null;

  propiedad?: {
    id: number;
    nombre?: string | null;
  } | null;
};

export type BlockMaintenance = {
  id: number;
  idPropiedad: number | null;
  tipo?: "blocked" | "maintenance" | null;
  fechaInicio: string | null; // YYYY-MM-DD
  fechaTermino: string | null; // YYYY-MM-DD
  horaInicio?: string | null; // HH:mm
  horaTermino?: string | null; // HH:mm
  noches?: number | null;
  razon?: string | null;
  notas?: string | null;
  /** true si lo creó un admin (cliente no puede tocar). */
  createdByAdmin?: boolean | null;
  /** id del user que creó el bloqueo — usado para distinguir visualmente
   * los bloqueos del propio cliente de los creados por admin/otros. */
  idUsuarioCreador?: number | null;
  /** FK al booking padre si es bloqueo AUTO de salida tardía. */
  reservaId?: number | null;
};

export type CalendarRow = {
  propertyId: number;
  propertyName: string;
  propertyColor?: string | null;

  trackIndex: number;

  /** solo reservas de este track (para pintar la barra continua) */
  bookings: Booking[];

  /** eventos no-reserva (aseos/bloqueos/mantenciones) asignados a este track */
  events: CalendarEventItem[];

  /** cache de celdas por fecha (O(1) en getCell) */
  cellsByDate: Record<string, CalendarCellData>;
};

export type Cleaner = {
  id: number;
  nombre: string;
  rut?: string;
  email?: string;
  telefono?: string;
  banco?: string;
  tipocuenta?: string;
  numerocuenta?: string;
  rol?: string;
  sector?: any;
};

export type CellState =
  | "empty"
  | "checkin"
  | "occupied"
  | "checkout"
  | "cleaning"
  | "blocked"
  | "maintenance";

export type CellThirdKey = "morning" | "mid" | "evening";

export type CellThirdData = {
  state: CellState;
  /** lista de eventos que caen en este tercio (normalmente 0 o 1 por track, por colisión) */
  events?: CalendarEventRef[];
};

export type CalendarEventRef =
  | {
      kind: "booking";
      /** id interno en BD (único) */
      bookingDbId?: number;
      bookingId?: string;
      plataforma?: string;
      codigoPlataforma?: string;
      /**
       * ID corto canónico de Stays (formato JS11J — `payload.id`). Solo
       * para reservas que vinieron por webhook. Lo mostramos en el modal
       * de detalle para cruzar con `/stays-webhooks`. Round 2026-05-07.
       */
      idReservaStays?: string;
      /** Fecha de creación de la reserva (ISO). Para mostrar "Creada el …" en el modal. */
      fechaCreacion?: string;
      huesped?: string;
      telefono?: string;
      reservaTotalClp?: number;
      precioPorNoche?: number;
      ingreso?: string;
      salida?: string;
      /**
       * Aseo AUTO vinculado a esta reserva (dirección inversa a la del aseo,
       * que ya trae `reservaId`). Es la ref COMPLETA del aseo (misma forma que
       * el evento cleaning de la celda), enriquecida en useCalendarioLogic vía
       * `cleaningRefByBookingId`. Solo aparece si la fila del aseo está cargada
       * en el rango visible. El modal de detalle de la RESERVA la inyecta como
       * tarjeta de aseo EDITABLE (dedup con la celda por `cleaning:{id}`).
       * `cleaningId` es el PK del aseo (`aseos.id`) — NUNCA el de la reserva.
       * Round 2026-07-11.
       */
      linkedCleaning?: CalendarEventRef;
    }
  | {
      kind: "cleaning";
      cleaningId: number;
      /**
       * FK al booking padre si es un aseo AUTO vinculado (`reserva_id`
       * en DB). Null/undefined = aseo manual sin vínculo. Se usa en
       * `checkAdminOverlap` para no marcar el aseo AUTO como conflicto
       * cuando se edita el propio booking.
       */
      reservaId?: number;
      /**
       * Huésped de la reserva padre (solo cuando es aseo AUTO vinculado
       * y la reserva está cargada). Permite mostrar "Aseo de Pedro …"
       * en el tooltip y card de detalle sin tener que cruzar manualmente
       * con la fila del booking. Lo enriquecemos en useCalendarioLogic
       * vía lookup `bookingsById`. Round 2026-05-30.
       */
      huespedReserva?: string;
      start?: string; // ISO datetime
      end?: string; // ISO datetime
      notas?: string;

      urgencia?: string;
      estado?: string;

      costoLimpieza?: number;
      sueldoAseador?: number;

      aseadorNombre?: string;
      aseadorTelefono?: string;
      aseadorEmail?: string;
    }
  | {
      kind: "blocked" | "maintenance";
      id: number;
      /** FK al booking padre (bloqueo AUTO de salida tardía). Mismo uso. */
      reservaId?: number;
      start?: string; // ISO datetime
      end?: string; // ISO datetime
      razon?: string;
      notas?: string;
      /**
       * Tri-estado del creador (undefined = null del backend = webhook de
       * Stays): true admin · false cliente · undefined Stays.
       */
      createdByAdmin?: boolean;
      /**
       * Usuario que creó el bloqueo/mantención. El cliente solo puede
       * editar/borrar los PROPIOS (createdByAdmin === false Y este id ===
       * su user.id) — regla de modulo-calendario.md §2, enforced también
       * en backend.
       */
      idUsuarioCreador?: number | null;
    };

type BookingPhase = "checkin" | "occupied" | "checkout";

export type CalendarCellData = {
  thirds: Record<CellThirdKey, CellThirdData>;
  date: string;
  idPropiedad: number;

  nombrePropiedad?: string;
  booking?: Booking | null; // <- el booking del track para ese día (si existe)
  bookingPhase?: BookingPhase;
  // detalle reserva (si aplica)
  checkin?: {
    reservaId?: string;
    plataforma?: string;
    codigoPlataforma?: string;
    huesped?: string;
    telefono?: string;
    reservaTotalClp?: number;
    precioPorNoche?: number;
  };

  checkout?: {
    reservaId?: string;
    plataforma?: string;
    codigoPlataforma?: string;
    huesped?: string;
    telefono?: string;
    reservaTotalClp?: number;
    precioPorNoche?: number;
  };

  // útil para detectar si hay algo clickeable
  hasAny?: boolean;
};

export type DateRange = { from: string; to: string };

/**
 * Meta del calendario Stays por propiedad (lo que alimenta las filas expandibles).
 *   - base:        precio base por noche (derivado del efectivo y la condición).
 *   - condicion:   string de display de la condición ("+10%", "-$5.000") o null.
 *   - condicionRaw: { tipo, valor } por fecha con condición — para editar.
 *   - efectivo:    precio efectivo (= base + condición) que está en Stays.
 *   - minNoches / restricciones: valores de Stays por noche.
 */
export type StaysCalendarMeta = {
  condicion: Record<string, any>;
  condicionRaw?: Record<string, { tipo: "porcentaje" | "monto"; valor: number }>;
  restricciones: Record<string, any>;
  minNoches: Record<string, any>;
  base: Record<string, any>;
  efectivo?: Record<string, any>;
};

type UseCalendarioLogicReturn = {
  loading: boolean;
  refreshing: boolean;
  error: string | null;

  range: DateRange;
  setRange: (r: DateRange) => void;
  /**
   * Mueve el calendario a un rango SIN guardarlo como el preferido del admin.
   * Es para el rango que impone un enlace directo (`?from=&to=`): él no lo
   * eligió, y si se guardara, la próxima visita sin parámetros abriría en esas
   * fechas. `restaurar:false` evita que lo guardado le gane al enlace; esto
   * evita lo inverso.
   */
  setRangeSinRecordar: (r: DateRange) => void;

  sidebarOpen: boolean;
  toggleSidebar: () => void;

  properties: Property[];
  /** Búsqueda libre por nombre de propiedad / cliente / propietario (filtra filas). */
  propSearch: string;
  setPropSearch: (q: string) => void;
  /**
   * Los tres `…SinRecordar` de los filtros de fila cambian la grilla SIN
   * guardar el valor como el preferido del admin. Son para lo que él no
   * eligió: el salto a un arriendo (buscador por código, enlace directo)
   * limpia estos filtros para que la propiedad destino no quede oculta, y
   * limpiarlos con el setter normal le borraría para siempre los filtros de
   * trabajo. Mismo criterio que `setRangeSinRecordar`.
   */
  setPropSearchSinRecordar: (q: string) => void;
  /** Búsqueda por nombre de arrendatario (huésped) — filtra propiedades con esa reserva. */
  guestSearch: string;
  setGuestSearch: (q: string) => void;
  setGuestSearchSinRecordar: (q: string) => void;
  /** Orden + filtros de las filas de propiedad (presentación). */
  propSort: PropSortKey;
  setPropSort: (k: PropSortKey) => void;
  propFilters: PropFilters;
  setPropFilters: (f: PropFilters) => void;
  setPropFiltersSinRecordar: (f: PropFilters) => void;
  /** Nombres únicos disponibles para los selects de filtro. */
  propFilterOptions: { region: string[]; comuna: string[]; sector: string[]; cliente: string[] };
  dates: string[];

  /** Bloqueos y mantenciones crudos — usados para render multi-día continuo. */
  blocks: BlockMaintenance[];

  /** Precios por noche (CLP) por propiedad y fecha: nightPrices[propertyId][YYYY-MM-DD] */
  nightPrices: Record<number, Record<string, number>>;
  /** Datos del calendario Stays por propiedad: condicion, restricciones, minNoches, base, efectivo */
  staysCalendarData: Record<number, StaysCalendarMeta>;
  fetchPropertyMeta: (propertyId: number) => Promise<void>;
  /**
   * Re-fetch FORZADO de la meta (base/minNoches/...) de UNA propiedad,
   * actualizándola en su lugar sin tocar bookings/blocks/cleanings ni la
   * meta de las demás propiedades. Se usa tras editar precios en Stays:
   * evita el `fetchCalendar` global (que resetea `staysCalendarData` y
   * colapsa las filas meta expandidas → salto de scroll) y refresca los
   * precios "Base ($)" mostrados.
   */
  refreshPropertyMeta: (propertyId: number) => Promise<void>;
  /**
   * Refresh SUAVE: re-trae eventos en su lugar, sin recargar la página ni
   * colapsar lo expandido. Con `includeMeta: true` (default) re-trae además
   * la meta (precios/minNoches/...) de todas las propiedades alguna vez
   * expandidas en la sesión/rango — cada meta golpea la API externa de
   * Stays, así que los paths que solo mueven EVENTOS (mutaciones de
   * arriendos/bloqueos/aseos, ticks WS) deben pasar `includeMeta: false`.
   * El botón "Refrescar" y el cambio masivo de precios usan el default.
   */
  refreshCalendar: (opts?: { includeMeta?: boolean }) => Promise<void>;
  focusedPropertyId: number | null;
  setFocusedPropertyId: (id: number | null) => void;
  getCell: (row: CalendarRow, date: string) => CalendarCellData;

  calendarRows: CalendarRow[];
  filteredAseadoras: Cleaner[];
  loadingAseadoras: boolean;
  fetchAseadoras: () => Promise<void>;
  refetch: () => Promise<void>;
  fetchEventDetail: (kind: string, id: number | string) => Promise<any>;
  // updateEvent: (payload: any) => Promise<any>;
  // deleteEvent: (kind: string, id: number | string) => Promise<boolean>;
};

function toISODate(d: Date): string {
  const y = d.getFullYear();
  const m = `${d.getMonth() + 1}`.padStart(2, "0");
  const day = `${d.getDate()}`.padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return toISODate(dt);
}

function buildDates(from: string, to: string): string[] {
  const res: string[] = [];
  let cur = from;
  while (cur <= to) {
    res.push(cur);
    cur = addDays(cur, 1);
  }
  return res;
}

function safeNumber(v: any): number | undefined {
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

function parseTimeToHM(t?: string | null): { h: number; m: number } | null {
  if (!t) return null;
  const parts = t.split(":");
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return { h, m };
}

function toDateTime(isoDate: string, time: { h: number; m: number }): Date {
  const [y, mo, d] = isoDate.split("-").map(Number);
  return new Date(y, mo - 1, d, time.h, time.m, 0, 0);
}

function defaultEmptyCell(
  propertyId: number,
  date: string,
  nombrePropiedad?: string,
): CalendarCellData {
  return {
    idPropiedad: propertyId,
    date,
    nombrePropiedad,
    thirds: {
      morning: { state: "empty" },
      mid: { state: "empty" },
      evening: { state: "empty" },
    },
    hasAny: false,
  };
}

/** Tercio horario fijo:
 * morning: [00:00, 11:00)
 * mid:     [11:00, 15:00)
 * evening: [15:00, 24:00)
 */
const THIRD_WINDOWS: Array<{
  key: CellThirdKey;
  startMin: number;
  endMin: number;
}> = [
  { key: "morning", startMin: 0, endMin: 11 * 60 },
  { key: "mid", startMin: 11 * 60, endMin: 15 * 60 },
  { key: "evening", startMin: 15 * 60, endMin: 24 * 60 },
];

function minutesOfDay(dt: Date) {
  return dt.getHours() * 60 + dt.getMinutes();
}

/** Retorna bitmask 1/2/4 según qué tercios intersecta el intervalo dentro de un mismo día */
function thirdsMaskForDayInterval(day: string, start: Date, end: Date): number {
  // recorta start/end a este día
  const dayStart = toDateTime(day, { h: 0, m: 0 });
  const dayEnd = toDateTime(day, { h: 23, m: 59 });

  const s = start < dayStart ? dayStart : start;
  const e = end > dayEnd ? dayEnd : end;

  const sMin = minutesOfDay(s);
  const eMin = minutesOfDay(e);

  let mask = 0;
  for (const w of THIRD_WINDOWS) {
    // Intersección de intervalos [sMin, eMin] con [w.startMin, w.endMin)
    // Usamos half-open para evitar doble conteo en bordes.
    const a0 = sMin;
    const a1 = eMin;
    const b0 = w.startMin;
    const b1 = w.endMin;

    const intersects = a0 < b1 && a1 > b0;
    if (intersects) {
      if (w.key === "morning") mask |= 1;
      if (w.key === "mid") mask |= 2;
      if (w.key === "evening") mask |= 4;
    }
  }
  return mask;
}

function thirdBounds(third: CellThirdKey): {
  start: { h: number; m: number };
  end: { h: number; m: number };
} {
  if (third === "morning")
    return { start: { h: 0, m: 0 }, end: { h: 11, m: 0 } };
  if (third === "mid") return { start: { h: 11, m: 0 }, end: { h: 15, m: 0 } };
  return { start: { h: 15, m: 0 }, end: { h: 23, m: 59 } };
}

function thirdFromUrgencia(
  urgencia?: string | null,
  fallbackHoraInicio?: string | null,
): CellThirdKey {
  const u = (urgencia ?? "").toString().trim().toLowerCase();

  // Reglas acordadas:
  // am -> morning
  // 11:00 y "critico 11:00" -> mid
  // pm -> evening
  if (u === "am" || u.includes(" am")) return "morning";
  if (u === "pm" || u.includes(" pm")) return "evening";
  if (u.includes("11:00") || u.includes("11")) return "mid";
  if (u.includes("critico")) return "mid";

  // fallback a horaInicio (si viene)
  const hm = parseTimeToHM(fallbackHoraInicio ?? undefined);
  if (hm) {
    const mins = hm.h * 60 + hm.m;
    if (mins < 11 * 60) return "morning";
    if (mins < 15 * 60) return "mid";
    return "evening";
  }

  // default central
  return "mid";
}

function cleaningIntervalFromUrgencia(
  c: Cleaning,
): { start: Date; end: Date } | null {
  const startDate = c.fecha ?? null;
  if (!startDate) return null;

  const endDate = c.fechaFin ?? startDate;
  const startThird = thirdFromUrgencia(c.urgencia, c.horaInicio ?? null);

  const startBounds = thirdBounds(startThird);
  const start = toDateTime(startDate, startBounds.start);

  // Si es multi-día: se extiende hasta el primer tercio de fechaFin (morning)
  if (endDate && endDate !== startDate) {
    const end = toDateTime(endDate, { h: 11, m: 0 }); // fin tercio morning (half-open)
    return { start, end };
  }

  const end = toDateTime(startDate, startBounds.end);
  return { start, end };
}

/**
 * Construye la ref COMPLETA de un aseo (kind:"cleaning") desde su fila cruda.
 * Misma forma y enriquecimiento que el evento cleaning que se arma por celda
 * (ver el bloque `ev` en itemsByProperty). Se usa para el mapa
 * `cleaningRefByBookingId` que alimenta la tarjeta de "Aseo vinculado" editable
 * del modal de detalle de la reserva. Round 2026-07-11.
 */
function buildCleaningEventRef(
  c: Cleaning,
  huespedByBookingId?: Map<number, string>,
): CalendarEventRef | null {
  const interval = cleaningIntervalFromUrgencia(c);
  if (!interval) return null;
  const huespedReserva =
    c.reservaId != null ? huespedByBookingId?.get(c.reservaId) : undefined;
  return {
    kind: "cleaning",
    cleaningId: c.id,
    reservaId: c.reservaId ?? undefined,
    huespedReserva,
    start: interval.start.toISOString(),
    end: interval.end.toISOString(),
    notas: c.observaciones ?? undefined,
    urgencia: c.urgencia ?? undefined,
    estado: c.estado ?? undefined,
    costoLimpieza: c.costoLimpieza ?? undefined,
    sueldoAseador: c.sueldoAseador ?? undefined,
    aseadorNombre: nombreEquipoAseo(c) ?? c.aseador?.nombre ?? undefined,
    aseadorTelefono: c.aseador?.telefono ?? undefined,
    aseadorEmail: c.aseador?.email ?? undefined,
  };
}

type NormalizedItem =
  | { kind: "booking"; propertyId: number; booking: Booking }
  | {
      kind: "cleaning";
      propertyId: number;
      cleaning: Cleaning;
      start: Date;
      end: Date;
    }
  | {
      kind: "blocked" | "maintenance";
      propertyId: number;
      block: BlockMaintenance;
      start: Date;
      end: Date;
    };

function buildBookingRefs(
  b: Booking,
  cleaningRefByBookingId?: Map<number, CalendarEventRef>,
): {
  base: CalendarEventRef;
  checkin: CalendarCellData["checkin"];
  checkout: CalendarCellData["checkout"];
} {
  // `b.precioNochesClp` en DB es el TOTAL (precio por noche × noches).
  // Derivamos el precio por noche dividiendo. Heurística anti-legacy: si
  // el cálculo da un valor absurdamente bajo (<1000 CLP), asumimos que
  // la columna ya guardó "por noche" (bug histórico helpers.js).
  const totalNoches = safeNumber(b.precioNochesClp) ?? 0;
  const nochesCount = safeNumber(b.noches) ?? 0;
  let precioPorNoche = 0;
  if (nochesCount > 0 && totalNoches > 0) {
    const candidato = Math.round(totalNoches / nochesCount);
    precioPorNoche = candidato >= 1000 ? candidato : totalNoches;
  } else {
    precioPorNoche = totalNoches;
  }
  const reservaTotalClp = safeNumber(b.reservaTotalClp);

  // Aseo AUTO vinculado (dirección reserva → aseo). Ref COMPLETA del aseo, solo
  // si su fila está cargada en el rango visible. El modal la inyecta como
  // tarjeta editable. `cleaningId` es el PK del aseo, NUNCA el id de la reserva.
  const linkedCleaning =
    b.id != null ? cleaningRefByBookingId?.get(b.id) : undefined;

  const base: CalendarEventRef = {
    kind: "booking",
    bookingDbId: b.id,
    bookingId: b.idReservaStays ?? b.idReservaPlataforma ?? undefined,
    plataforma: b.nombrePlataforma ?? undefined,
    codigoPlataforma: b.idReservaPlataforma ?? undefined,
    // El modal de detalle muestra este código como «Ref. plataforma» (solo
    // cuando la reserva vino de Stays/Airbnb/Booking → `id_reservac`, JS11J).
    idReservaStays: b.idReservaStays ?? undefined,
    fechaCreacion: b.fechaCreacion ?? undefined,
    huesped: b.nombreHuesped ?? undefined,
    telefono: b.telefonoHuesped ?? undefined,
    reservaTotalClp,
    precioPorNoche,
    ingreso: b.ingreso ?? undefined,
    salida: b.salida ?? undefined,
    linkedCleaning,
  };

  return {
    base,
    checkin: {
      reservaId: b.idReservaStays ?? undefined,
      plataforma: b.nombrePlataforma ?? undefined,
      codigoPlataforma: b.idReservaPlataforma ?? undefined,
      huesped: b.nombreHuesped ?? undefined,
      telefono: b.telefonoHuesped ?? undefined,
      precioPorNoche,
      reservaTotalClp,
    },
    checkout: {
      reservaId: b.idReservaStays ?? undefined,
      plataforma: b.nombrePlataforma ?? undefined,
      codigoPlataforma: b.idReservaPlataforma ?? undefined,
      huesped: b.nombreHuesped ?? undefined,
      telefono: b.telefonoHuesped ?? undefined,
      precioPorNoche,
      reservaTotalClp,
    },
  };
}

function pushEventToThird(
  cell: CalendarCellData,
  third: CellThirdKey,
  state: CellState,
  ev: CalendarEventRef,
) {
  const t = cell.thirds[third];
  // Si por alguna razón ya había algo (debería evitarse por colisiones), dejamos el primero y agregamos a lista.
  const prevState = t.state;
  if (prevState === "empty") {
    t.state = state;
  } else if (prevState !== state) {
    // Si cae un segundo tipo en el mismo tercio por data mala, lo mantenemos igual pero lo registramos en events.
  }
  t.events = [...(t.events ?? []), ev];
  cell.hasAny = true;
}

function thirdsFromMask(mask: number): CellThirdKey[] {
  const res: CellThirdKey[] = [];
  if (mask & 1) res.push("morning");
  if (mask & 2) res.push("mid");
  if (mask & 4) res.push("evening");
  return res;
}

/**
 * Construye tracks por propiedad usando colisión por (fecha, tercio).
 * - Si un item ocupa un tercio que ya está ocupado en un track, se va a otro track.
 * - Los tracks siempre visibles.
 */
function buildTracksForProperty(
  property: Property,
  items: NormalizedItem[],
  dates: string[],
  /**
   * Mapa global de bookingId → huesped, construido en useCalendarioLogic
   * antes de invocar este helper. Lo usamos para enriquecer los aseos
   * AUTO con el huésped de su reserva padre, incluso si la reserva está
   * en OTRA propiedad (caso histórico del bug id_propiedad mismatch).
   * Round 2026-05-30.
   */
  huespedByBookingId?: Map<number, string>,
  /**
   * Mapa bookingId → ref COMPLETA del aseo AUTO vinculado. Enriquece el ref de
   * la RESERVA con el aseo vinculado (para el modal de detalle). Round 2026-07-11.
   */
  cleaningRefByBookingId?: Map<number, CalendarEventRef>,
): CalendarRow[] {
  type Track = {
    occupiedMasksByDate: Record<string, number>;
    bookings: Booking[];
    events: CalendarEventItem[];
    cellsByDate: Record<string, CalendarCellData>;
  };

  const tracks: Track[] = [];

  const tryPlace = (
    track: Track,
    occByDate: Array<{ date: string; mask: number; apply: () => void }>,
  ) => {
    for (const seg of occByDate) {
      const cur = track.occupiedMasksByDate[seg.date] ?? 0;
      if ((cur & seg.mask) !== 0) return false;
    }
    // place
    for (const seg of occByDate) {
      track.occupiedMasksByDate[seg.date] =
        (track.occupiedMasksByDate[seg.date] ?? 0) | seg.mask;
      seg.apply();
    }
    return true;
  };

  const placeItem = (item: NormalizedItem) => {
    // Construye segmentos por día (mask por fecha)
    const occByDate: Array<{
      date: string;
      mask: number;
      apply: () => void;
    }> = [];

    if (item.kind === "booking") {
      const b = item.booking;
      const ingreso = b.ingreso ?? null;
      const salida = b.salida ?? null;
      if (!ingreso || !salida) return;

      const refs = buildBookingRefs(b, cleaningRefByBookingId);

      for (const date of dates) {
        if (date < ingreso || date > salida) continue;

        if (date === ingreso) {
          occByDate.push({
            date,
            mask: 4,
            apply: () => {},
          });
        } else if (date === salida) {
          occByDate.push({
            date,
            mask: 1,
            apply: () => {},
          });
        } else {
          occByDate.push({
            date,
            mask: 1 | 2 | 4,
            apply: () => {},
          });
        }
      }

      const buildApply = (track: Track) => {
        for (let i = 0; i < occByDate.length; i++) {
          const seg = occByDate[i];
          occByDate[i] = {
            ...seg,
            apply: () => {
              const cell =
                track.cellsByDate[seg.date] ??
                defaultEmptyCell(property.id, seg.date, property.nombre);
              track.cellsByDate[seg.date] = cell;

              // ✅ SIEMPRE setear el booking del track
              cell.booking = b;

              if (seg.date === ingreso) {
                cell.bookingPhase = "checkin";
                cell.checkin = refs.checkin;
                pushEventToThird(cell, "evening", "checkin", refs.base);
              } else if (seg.date === salida) {
                cell.bookingPhase = "checkout";
                cell.checkout = refs.checkout;
                pushEventToThird(cell, "morning", "checkout", refs.base);
              } else {
                // ✅ Día intermedio: "occupied" pero con mismos datos disponibles
                cell.bookingPhase = "occupied";

                // ✅ copiar ambos para que el modal muestre lo mismo
                cell.checkin = refs.checkin;
                cell.checkout = refs.checkout;

                for (const third of [
                  "morning",
                  "mid",
                  "evening",
                ] as CellThirdKey[]) {
                  pushEventToThird(cell, third, "occupied", refs.base);
                }
              }
            },
          };
        }
      };

      // Intenta colocar en tracks existentes / crear uno
      let placed = false;
      for (const t of tracks) {
        buildApply(t);
        if (tryPlace(t, occByDate)) {
          t.bookings.push(b);
          placed = true;
          break;
        }
      }
      if (!placed) {
        const t: Track = {
          occupiedMasksByDate: {},
          bookings: [],
          events: [],
          cellsByDate: {},
        };
        buildApply(t);
        tryPlace(t, occByDate);
        t.bookings.push(b);
        tracks.push(t);
      }
      return;
    }

    if (item.kind === "cleaning") {
      const c = item.cleaning;
      const startDate = c.fecha ?? null;
      const endDate = c.fechaFin ?? c.fecha ?? null;
      if (!startDate || !endDate) return;

      // intersection con rango visible
      const visibleDates = dates.filter((d) => d >= startDate && d <= endDate);
      if (visibleDates.length === 0) return;

      const start = item.start;
      const end = item.end;

      // Lookup del huésped del booking padre para aseos AUTO. Si la
      // reserva no está en el dataset (porque está fuera del rango
      // visible o todavía no se cargó), queda undefined y el render
      // muestra "Aseo AUTO" sin el nombre.
      const huespedReserva =
        c.reservaId != null
          ? huespedByBookingId?.get(c.reservaId)
          : undefined;

      const ev: CalendarEventRef = {
        kind: "cleaning",
        cleaningId: c.id,
        reservaId: c.reservaId ?? undefined,
        huespedReserva,
        start: start.toISOString(),
        end: end.toISOString(),
        notas: c.observaciones ?? undefined,

        urgencia: c.urgencia ?? undefined,
        estado: c.estado ?? undefined,

        costoLimpieza: c.costoLimpieza ?? undefined,
        sueldoAseador: c.sueldoAseador ?? undefined,

        aseadorNombre: nombreEquipoAseo(c) ?? c.aseador?.nombre ?? undefined,
        aseadorTelefono: c.aseador?.telefono ?? undefined,
        aseadorEmail: c.aseador?.email ?? undefined,
      };

      const segs = visibleDates
        .map((d) => {
          const mask = thirdsMaskForDayInterval(d, start, end);
          const thirds = thirdsFromMask(mask);
          return {
            date: d,
            mask,
            apply: (track: Track) => {
              const cell =
                track.cellsByDate[d] ??
                defaultEmptyCell(property.id, d, property.nombre);
              track.cellsByDate[d] = cell;
              for (const th of thirds) {
                pushEventToThird(cell, th, "cleaning", ev);
              }
            },
          };
        })
        .filter((s) => s.mask !== 0);

      if (segs.length === 0) return;

      let placed = false;
      for (const t of tracks) {
        const occ = segs.map((s) => ({
          date: s.date,
          mask: s.mask,
          apply: () => s.apply(t),
        }));
        if (tryPlace(t, occ)) {
          t.events.push({
            kind: "cleaning",
            id: c.id,
            propertyId: property.id,
            startISO: start.toISOString(),
            endISO: end.toISOString(),
            notas: c.observaciones ?? undefined,
            urgencia: c.urgencia ?? undefined,
            aseadorNombre: nombreEquipoAseo(c) ?? c.aseador?.nombre ?? undefined,
            reservaId: c.reservaId ?? undefined,
            huespedReserva,
          });
          placed = true;
          break;
        }
      }
      if (!placed) {
        const t: Track = {
          occupiedMasksByDate: {},
          bookings: [],
          events: [],
          cellsByDate: {},
        };
        const occ = segs.map((s) => ({
          date: s.date,
          mask: s.mask,
          apply: () => s.apply(t),
        }));
        tryPlace(t, occ);
        t.events.push({
          kind: "cleaning",
          id: c.id,
          propertyId: property.id,
          startISO: start.toISOString(),
          endISO: end.toISOString(),
          notas: c.observaciones ?? undefined,
          urgencia: c.urgencia ?? undefined,
          aseadorNombre: nombreEquipoAseo(c) ?? c.aseador?.nombre ?? undefined,
        });
        tracks.push(t);
      }
      return;
    }

    const bm = item.block;
    const startDate = bm.fechaInicio ?? null;
    const endDate = bm.fechaTermino ?? null;
    if (!startDate || !endDate) return;

    // intersection with visible dates
    const visibleDates = dates.filter((d) => d >= startDate && d <= endDate);
    if (visibleDates.length === 0) return;

    const kind = item.kind;

    const ev: CalendarEventRef = {
      kind,
      id: bm.id,
      reservaId: (bm as any).reservaId ?? undefined,
      start: item.start.toISOString(),
      end: item.end.toISOString(),
      razon: bm.razon ?? undefined,
      notas: bm.notas ?? undefined,
      createdByAdmin: bm.createdByAdmin ?? undefined,
      idUsuarioCreador: bm.idUsuarioCreador ?? null,
    };

    const segs = visibleDates
      .map((d) => {
        const mask = thirdsMaskForDayInterval(d, item.start, item.end);
        const thirds = thirdsFromMask(mask);
        return {
          date: d,
          mask,
          apply: (track: Track) => {
            const cell =
              track.cellsByDate[d] ??
              defaultEmptyCell(property.id, d, property.nombre);
            track.cellsByDate[d] = cell;
            for (const th of thirds) {
              pushEventToThird(cell, th, kind, ev);
            }
          },
        };
      })
      .filter((s) => s.mask !== 0);

    if (segs.length === 0) return;

    let placed = false;
    for (const t of tracks) {
      const occ = segs.map((s) => ({
        date: s.date,
        mask: s.mask,
        apply: () => s.apply(t),
      }));
      if (tryPlace(t, occ)) {
        // MISMOS campos que el push del track nuevo (más abajo): la grilla
        // admin clasifica color/badge desde `row.events` — si esta rama (la
        // común: los bookings crean el track 0 primero) omite
        // `createdByAdmin`, todo bloqueo que comparte track se pintaba
        // oscuro con badge "Desde Stays" aunque fuera de admin o cliente
        // (fix 2026-07-02).
        t.events.push({
          kind,
          id: bm.id,
          propertyId: property.id,
          startISO: item.start.toISOString(),
          endISO: item.end.toISOString(),
          razon: bm.razon ?? undefined,
          notas: bm.notas ?? undefined,
          createdByAdmin: bm.createdByAdmin ?? undefined,
          idUsuarioCreador: bm.idUsuarioCreador ?? null,
        });
        placed = true;
        break;
      }
    }
    if (!placed) {
      const t: Track = {
        occupiedMasksByDate: {},
        bookings: [],
        events: [],
        cellsByDate: {},
      };
      const occ = segs.map((s) => ({
        date: s.date,
        mask: s.mask,
        apply: () => s.apply(t),
      }));
      tryPlace(t, occ);
      t.events.push({
        kind,
        id: bm.id,
        propertyId: property.id,
        startISO: item.start.toISOString(),
        endISO: item.end.toISOString(),
        razon: bm.razon ?? undefined,
        notas: bm.notas ?? undefined,
        createdByAdmin: bm.createdByAdmin ?? undefined,
        idUsuarioCreador: bm.idUsuarioCreador ?? null,
      });
      tracks.push(t);
    }
  };

  // ✅ PRIORIZA BOOKINGS (pero NO restringe el track 0 solo a bookings)
  const bookingItems = items.filter((i) => i.kind === "booking");
  const otherItems = items.filter((i) => i.kind !== "booking");

  for (const item of bookingItems) placeItem(item);
  for (const item of otherItems) placeItem(item);

  // Si no hay tracks, genera 1 vacío
  if (tracks.length === 0) {
    return [
      {
        propertyId: property.id,
        propertyName: property.nombre,
        propertyColor: property.color,
        trackIndex: 0,
        bookings: [],
        events: [],
        cellsByDate: {},
      },
    ];
  }

  return tracks.map((t, idx) => ({
    propertyId: property.id,
    propertyName: property.nombre,
    propertyColor: property.color,
    trackIndex: idx,
    bookings: t.bookings,
    events: t.events,
    cellsByDate: t.cellsByDate,
  }));
}

// Auto-reintento del refresh en background cuando el GET falla (blip de red,
// redeploy del backend en Railway, 500 transitorio). 4 reintentos × 8 s cubren
// ~32 s de caída; ventanas más largas las cubre el resync al reconectar el WS.
const MAX_REFRESH_RETRIES = 4;
const REFRESH_RETRY_MS = 8_000;

// ── Filtros recordados (round 2026-09-24) ────────────────────────────────
// El rango, las dos búsquedas, el orden y los cuatro multiselect sobreviven a
// la recarga y al cambio de página. Solo para admin/superadmin: de eso se
// encarga useFiltroPersistido, así que acá no hay condicional de rol aunque
// este mismo hook monte también la vista del cliente.
// Todo lo que se restaura se valida: un valor viejo (de otra versión de la
// app, o editado a mano) no puede dejar la grilla en un estado imposible.

const ORDENES_PROPIEDAD: PropSortKey[] = [
  "nombre",
  "region",
  "comuna",
  "sector",
  "cliente",
];

const DIMENSIONES_FILTRO = ["region", "comuna", "sector", "cliente"] as const;

const ES_DIA_ISO = /^\d{4}-\d{2}-\d{2}$/;

const esTexto = (valor: unknown): boolean => typeof valor === "string";

/** Las cuatro dimensiones, cada una con una lista de nombres. */
const esFiltroDePropiedades = (valor: unknown): boolean =>
  !!valor &&
  typeof valor === "object" &&
  !Array.isArray(valor) &&
  DIMENSIONES_FILTRO.every((k) => {
    const lista = (valor as Record<string, unknown>)[k];
    return Array.isArray(lista) && lista.every((n) => typeof n === "string");
  });

/**
 * Rango guardado → rango usable: dos fechas `YYYY-MM-DD` en orden y siempre
 * acotado al tope de 14 meses. Un rango más largo hace que el backend responda
 * 400 y la fila de Mercado quede vacía sin explicación.
 */
const deserializarRangoCalendario = (crudo: unknown): DateRange | undefined => {
  const r = crudo as { from?: unknown; to?: unknown } | null;
  if (!r || typeof r.from !== "string" || typeof r.to !== "string") return undefined;
  if (!ES_DIA_ISO.test(r.from) || !ES_DIA_ISO.test(r.to) || r.from > r.to) {
    return undefined;
  }
  return acotarRangoCalendario({ from: r.from, to: r.to }).rango;
};

/**
 * ¿La URL trae el rango puesto? Los enlaces directos de /arriendos, Inicio y
 * Alertas (`?from=&to=`) mandan sobre el rango guardado: quien llega por ahí
 * tiene que ver ESAS fechas. Se lee de `window.location` y no del router
 * porque este hook también se monta en pruebas sin Router.
 */
function hayRangoEnLaUrl(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get("from") && params.get("to"));
  } catch {
    return false;
  }
}

/**
 * ¿La URL apunta a UNA propiedad (`?propertyId=`)? Los enlaces de Inicio,
 * Alertas, /arriendos y /finanzas-empresa saltan a esa propiedad: si los
 * filtros de fila guardados la dejan fuera de la grilla, el salto falla en
 * silencio (no hay fila, no hay scroll ni resaltado). Mismo criterio que el
 * rango: el enlace manda sobre lo guardado.
 */
function hayPropiedadEnLaUrl(): boolean {
  try {
    return Boolean(new URLSearchParams(window.location.search).get("propertyId"));
  } catch {
    return false;
  }
}

export function useCalendarioLogic(): UseCalendarioLogicReturn & {
  calendarRows: CalendarRow[];
} {
  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return toISODate(now);
  }, []);

  const [loading, setLoading] = useState<boolean>(true);
  // Refresh silencioso: fetch en background sin tapar la UI con spinner.
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [properties, setProperties] = useState<Property[]>([]);
  // Llegamos por un enlace que apunta a UNA propiedad: los filtros de fila
  // guardados no se restauran en este montaje (la ocultarían y el salto
  // quedaría mudo). Se evalúa UNA sola vez, igual que el del rango: el efecto
  // del deep-link limpia la query después de aplicarla, y aunque hoy el hook
  // ya congela `restaurar` por su cuenta, dejar los dos criterios idénticos
  // evita que mañana dependan de en qué render se lean.
  const [hayEnlaceDirectoDePropiedad] = useState(hayPropiedadEnLaUrl);
  // Orden + filtros de las filas de propiedad (solo presentación; no re-fetchea).
  // Búsqueda libre por nombre de propiedad / cliente / propietario.
  const [propSearch, setPropSearch, setPropSearchSinRecordar] =
    useFiltroPersistido<string>("calendario.busquedaPropiedad", "", {
      validar: esTexto,
      restaurar: !hayEnlaceDirectoDePropiedad,
    });
  // Búsqueda por nombre de arrendatario (huésped): filtra las propiedades que
  // tienen una reserva con ese huésped en el rango cargado. Caduca junto con
  // el rango: solo matchea huéspedes de reservas DENTRO del rango, así que un
  // texto viejo sin su rango dejaría la grilla vacía.
  const [guestSearch, setGuestSearch, setGuestSearchSinRecordar] =
    useFiltroPersistido<string>("calendario.busquedaHuesped", "", {
      validar: esTexto,
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      restaurar: !hayEnlaceDirectoDePropiedad,
    });
  const [propSort, setPropSort] = useFiltroPersistido<PropSortKey>(
    "calendario.ordenPropiedad",
    "nombre",
    { validar: (v) => ORDENES_PROPIEDAD.includes(v as PropSortKey) },
  );
  const [propFilters, setPropFilters, setPropFiltersSinRecordar] =
    useFiltroPersistido<PropFilters>(
      "calendario.filtrosPropiedad",
      { region: [], comuna: [], sector: [], cliente: [] },
      {
        validar: esFiltroDePropiedades,
        restaurar: !hayEnlaceDirectoDePropiedad,
      },
    );
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [cleanings, setCleanings] = useState<Cleaning[]>([]);
  const [blocks, setBlocks] = useState<BlockMaintenance[]>([]);
  const [nightPrices, setNightPrices] = useState<
    Record<number, Record<string, number>>
  >({});
  const [staysCalendarData, setStaysCalendarData] = useState<
    Record<number, any>
  >({});
  const [propertyMetaCache, setPropertyMetaCache] = useState<Set<number>>(new Set());
  const { sidebarOpen, toggleSidebar } = useSidebar();
  const [aseadoras, setAseadoras] = useState<Cleaner[]>([]);
  const [loadingAseadoras, setLoadingAseadoras] = useState(false);
  // El setter no se usa (la búsqueda se inicializa vacía y no muta).
  const [aseadorasSearchText] = useState("");
  const [focusedPropertyId, setFocusedPropertyId] = useState<number | null>(
    null,
  );
  const { user } = useAuth();
  const isAdmin = isAdminRole(user?.rol);

  // Se evalúa UNA sola vez: el efecto del deep-link limpia la query después de
  // aplicarla, y si esto se re-evaluara, «ya no hay enlace» reabriría la
  // restauración encima del rango que acaba de poner el enlace.
  const [hayEnlaceDirectoDeRango] = useState(hayRangoEnLaUrl);

  const [range, setRange, setRangeSinRecordar] = useFiltroPersistido<DateRange>(
    "calendario.rango",
    () => {
      if (isAdmin) {
        return { from: addDays(today, -5), to: addDays(today, 15) };
      }

      const m = ahoraChile();
      return {
        from: m.startOf("month").format("YYYY-MM-DD"),
        to: m.endOf("month").format("YYYY-MM-DD"),
      };
    },
    {
      // Caduca a los 7 días: el default es relativo a hoy (hoy-5 .. hoy+15) y
      // volver semanas después a un rango viejo muestra otra cosa sin avisar.
      caducaEnDias: CADUCIDAD_FECHAS_DIAS,
      deserializar: deserializarRangoCalendario,
      restaurar: !hayEnlaceDirectoDeRango,
    },
  );

  const dates = useMemo(() => buildDates(range.from, range.to), [range]);

  const filteredAseadoras = useMemo(() => {
    if (!aseadorasSearchText) return aseadoras;
    const q = aseadorasSearchText.toLowerCase();
    return aseadoras.filter((a) => (a.nombre ?? "").toLowerCase().includes(q));
  }, [aseadoras, aseadorasSearchText]);

  const fetchAseadoras = useCallback(async () => {
    try {
      setLoadingAseadoras(true);

      const data = await api.usersApi.getCleaners();

      if (data) {
        // `getCleaners` ya devuelve shape mapeado: {id, nombre, rut, email,
        // telefono, banco, tipocuenta, numerocuenta, rol, sector}. Leer
        // `nombre_usuario`, `rut_usuario` acá devolvía undefined y el Select
        // del modal mostraba aseadores sin nombre / no elegibles — la lista
        // también divergía de /aseos, que usa la misma API con el mapping
        // correcto.
        const mapped: Cleaner[] = data.map((cleaner: any) => ({
          id: cleaner.id,
          nombre: cleaner.nombre ?? "",
          rut: cleaner.rut ?? "",
          email: cleaner.email ?? "",
          telefono: cleaner.telefono || "",
          banco: cleaner.banco || "",
          tipocuenta: cleaner.tipocuenta || "",
          numerocuenta: cleaner.numerocuenta || "",
          rol: cleaner.rol,
          sector: cleaner.sector,
        }));
        setAseadoras(mapped);
        return;
      }

      // Si getCleaners devuelve null (backend down o 401) no intentamos el
      // endpoint legacy `/aseadoras` — no existe en las rutas actuales y
      // siempre devolvía 404, generando ruido en consola. Dejamos la lista
      // vacía y el mensaje de error al apiFetch interceptor.
      setAseadoras([]);
    } catch (e) {
      console.error("Error fetching aseadoras:", e);
      getMsg()?.error("Error al cargar aseadoras");
    } finally {
      setLoadingAseadoras(false);
    }
  }, []);
  /**
   * Fetch dedicado de la lista de propiedades. Corre una sola vez por sesión
   * (cambio de usuario), no cada vez que el cliente cambia de propiedad en
   * el selector — eso era la causa principal de la lentitud del calendario.
   */
  const fetchProperties = useCallback(async () => {
    try {
      const userId = user?.id != null ? String(user.id) : null;
      // `user.rol` es string escalar (ver CLAUDE.md §3.3). `normalizeRole`
      // mapea legacy ("propietario"/"usuario"/"otro" → "cliente") y acepta
      // defensivamente array legacy de tokens viejos. Ver WARNING en
      // roles.utils.ts antes de tocar.
      const userRol = normalizeRole(user?.rol);
      const userIsAdmin = isAdminRole(userRol);
      const isOwnerRole = userRol === "cliente";

      // limit=400 (máximo del backend): sin él la grilla y el buscador del
      // calendario truncaban en silencio al superar el default.
      const pJson = await apiFetch<any>("/propiedades?limit=400");
      if (!pJson) throw new Error("Error propiedades");
      const pData: any[] = Array.isArray(pJson) ? pJson : (pJson.data ?? []);

      const propiedades: Property[] = pData
        .filter((p) => {
          if (!userId) return false;
          if (userIsAdmin) return true;
          if (isOwnerRole) return String(p.id_cliente) === userId;
          return false;
        })
        .filter((p) => p.nombrepropiedad)
        .filter((p) => p.habilitado !== false)
        .map((p) => mapPropiedadCalendario(p, userIsAdmin));

      // Orden FIJO alfabético (A→Z) por nombre de propiedad. Las propiedades
      // no se pueden reordenar manualmente: siempre se listan de arriba hacia
      // abajo en orden alfabético. `localeCompare` con sensibilidad "base"
      // ignora mayúsculas/acentos para un orden natural en español.
      propiedades.sort((a, b) =>
        (a.nombre ?? "").localeCompare(b.nombre ?? "", "es", {
          sensitivity: "base",
          numeric: true,
        }),
      );

      setProperties(propiedades);
    } catch (e: any) {
      setError(e?.message ?? "Error desconocido");
    }
  }, [user?.id, user?.rol]);

  // ─── Resiliencia del refresh en background (round 2026-07-03) ────────────
  // `hasLoadedOnceRef` distingue la carga INICIAL (fallo ⇒ pantalla de error)
  // de un refresh posterior (fallo ⇒ conservar la data visible + reintento).
  // No sirve `isInitial` para esto: por el closure stale documentado dentro
  // de fetchCalendar, para admin ese flag es SIEMPRE true.
  const hasLoadedOnceRef = useRef(false);
  // Mirror del rango vigente para los reintentos: fetchCalendar recibe el
  // rango por parámetro y su closure no lo tiene — reintentar con el rango
  // del fallo pisaría la vista si el usuario lo cambió mientras tanto.
  const rangeRef = useRef(range);
  rangeRef.current = range;
  const refreshRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const refreshRetryCountRef = useRef(0);
  // Self-ref: el setTimeout del reintento debe llamar a la VERSIÓN VIGENTE
  // de fetchCalendar (la del closure estaría stale si cambiaron sus deps).
  const fetchCalendarSelfRef = useRef<
    ((r: DateRange, opts?: { preserveMeta?: boolean }) => Promise<void>) | null
  >(null);
  // Dedupe de fetches EN VUELO (round 2026-07-03): tras crear/editar/borrar
  // un evento, el submit refetchea Y ~400 ms después el tick WS de la propia
  // mutación refetchea de nuevo → 2 GETs idénticos (para el cliente, cada uno
  // con round-trips a APIs externas en el backend). Si ya corre un fetch del
  // mismo rango, se reutiliza su promesa en vez de duplicar el request.
  const inFlightFetchRef = useRef<{
    key: string;
    hard: boolean;
    promise: Promise<void>;
  } | null>(null);
  // Guardia anti-carrera (round 2026-07-05): al navegar meses MUY rápido
  // quedan varios fetch de RANGOS DISTINTOS en vuelo. El dedupe de arriba solo
  // colapsa fetches del MISMO rango; no impide que una respuesta vieja llegue
  // tarde y pise la data del rango vigente → el calendario queda "cargado pero
  // vacío" aunque haya eventos. Cada fetch real toma un `seq` monotónico; solo
  // el MÁS NUEVO aplica su resultado (los superados se descartan).
  const fetchSeqRef = useRef(0);

  /**
   * Fetch del calendario (bookings + blocks + cleanings + nightPrices).
   * Ignora la lista de propiedades — esa se carga aparte vía fetchProperties.
   *
   * Importante:
   *  - Si el cliente aún no tiene propiedad enfocada, HACEMOS EARLY-RETURN
   *    SIN TOCAR `loading`. Así la UI mantiene el spinner inicial y no
   *    parpadea una vista vacía mientras el hook auto-selecciona la
   *    primera propiedad.
   *  - En refreshes (mutaciones) usamos `refreshing=true` en vez de
   *    `loading=true` para no borrar la UI mientras el backend responde.
   */
  const fetchCalendar = useCallback(
    async (r: DateRange, opts: { preserveMeta?: boolean } = {}) => {
      const userId = user?.id != null ? String(user.id) : null;
      // `user.rol` escalar post-refactor 2026-04 (ver CLAUDE.md §3.3).
      // Se pasa directo a isAdminRole(), que ya acepta string|string[]|null.
      // Un admin mal-detectado aquí provocaría que shouldFetchCalendar quede
      // en false y nunca se traiga la data de todas las propiedades.
      const userIsAdmin = isAdminRole(user?.rol);

      const shouldFetchCalendar = userIsAdmin || !!focusedPropertyId;
      if (!shouldFetchCalendar) {
        // No apagamos `loading` — seguimos esperando el auto-select de
        // propiedad. Retornamos silenciosamente.
        return;
      }

      // Dedupe: si ya hay un fetch EN VUELO equivalente, reutilizarlo — salvo
      // que el nuevo sea DURO (resetea la meta admin) y el que corre sea suave:
      // un suave no puede reemplazar la semántica del duro.
      //
      // La clave DEBE reflejar TODO lo que varía en la URL del request (rango +
      // propiedad enfocada + usuario), no solo el rango. Si el cliente cambia
      // de propiedad con el MISMO mes mientras un fetch está en vuelo, ese
      // request es DISTINTO (otra propiedad → otra data) y NO debe colapsarse
      // contra el anterior: si se deduplica, se aplica la respuesta de la
      // propiedad vieja y el grid —que filtra por la propiedad nueva— queda
      // "cargado pero vacío". Con el scope en la clave, el fetch nuevo dispara,
      // toma su propio seq, y la guarda de secuencia descarta la respuesta
      // vieja. (Round 2026-07-05, hallazgo de revisión adversarial.)
      const dedupeScope = userIsAdmin ? "all" : (focusedPropertyId ?? "none");
      const dedupeKey = `${dedupeScope}|${userId ?? ""}|${r.from}|${r.to}`;
      const isHard = !opts.preserveMeta;
      const inFlight = inFlightFetchRef.current;
      if (inFlight && inFlight.key === dedupeKey && (inFlight.hard || !isHard)) {
        return inFlight.promise;
      }
      // Registro del fetch en vuelo: promesa diferida que se resuelve en el
      // `finally` (así los llamadores deduplicados esperan el mismo ciclo).
      let resolveInFlight!: () => void;
      const inFlightPromise = new Promise<void>((res) => {
        resolveInFlight = res;
      });
      inFlightFetchRef.current = {
        key: dedupeKey,
        hard: isHard,
        promise: inFlightPromise,
      };

      // Secuencia de ESTE fetch (ya pasamos el dedupe → es un request real).
      // Al terminar, solo aplicamos su resultado si sigue siendo el más nuevo.
      const mySeq = ++fetchSeqRef.current;

      // Primera carga vs refresh: sólo mostramos el spinner de pantalla
      // completa en la primera vez.
      //
      // OJO con el closure stale: este useCallback tiene deps
      // [focusedPropertyId, user?.id, user?.rol], que para admin NO cambian
      // tras el primer render. Por eso `bookings/blocks/cleanings` quedan
      // congelados en sus arrays vacíos iniciales y `isInitial` es SIEMPRE
      // true. Sin la guarda `!opts.preserveMeta`, el refresh SUAVE (botón
      // "Refrescar", que pasa preserveMeta=true) disparaba setLoading(true)
      // → el early-return `if (loading)` de calendario.tsx desmonta toda la
      // grilla → se pierde el estado local `expandedProperties` de
      // CalendarioGrid → las filas expandidas se colapsaban. El scroll sí se
      // restauraba (usa un ref aparte), de ahí el síntoma "vuelve a su lugar
      // pero cierra lo expandido". En el refresh suave nunca queremos el
      // spinner full-screen: el velo `refreshing` ya da feedback sin remontar.
      const isInitial = bookings.length === 0 && blocks.length === 0 && cleanings.length === 0;
      if (isInitial && !opts.preserveMeta) setLoading(true);
      setRefreshing(true);
      setError(null);

      try {
        const qUser = userId ? `&userId=${encodeURIComponent(userId)}` : "";
        const qProperty =
          !userIsAdmin && focusedPropertyId
            ? `&propertyId=${encodeURIComponent(String(focusedPropertyId))}`
            : "";

        // `silent`: el manejo de error es NUESTRO — en carga inicial el Alert
        // fatal ya lo muestra, y en refresh de fondo avisamos discreto sin
        // duplicar el toast genérico de apiFetch.
        const bJson = await apiFetch<any>(
          `/stays/calendario?from=${encodeURIComponent(r.from)}&to=${encodeURIComponent(r.to)}${qUser}${qProperty}`,
          { silent: true },
        );
        if (!bJson) throw new Error("Error reservas");

        // Anti-carrera: si un fetch MÁS NUEVO arrancó mientras esperábamos
        // esta respuesta, la descartamos. Aplicarla pisaría la data del rango
        // que el usuario está viendo ahora → "cargado pero vacío" al navegar
        // meses rápido. El fetch vigente escribirá la data correcta.
        if (mySeq !== fetchSeqRef.current) return;

        const bData: Booking[] = Array.isArray(bJson)
          ? bJson
          : (bJson.bookings ?? []);
        const blData: BlockMaintenance[] = Array.isArray(bJson)
          ? bJson
          : (bJson.blocks ?? []);
        const clData: Cleaning[] = Array.isArray(bJson)
          ? bJson
          : (bJson.cleanings ?? []);
        const npData: any = Array.isArray(bJson)
          ? null
          : (bJson.nightPrices ?? null);
        const scData: any = Array.isArray(bJson)
          ? null
          : (bJson.staysCalendarData ?? null);

        setBookings(bData);
        setBlocks(blData);
        setCleanings(clData);
        // Fetch OK: desde ahora los fallos se tratan como "refresh de fondo"
        // (conservar vista + reintento), no como error fatal de carga.
        hasLoadedOnceRef.current = true;
        refreshRetryCountRef.current = 0;
        if (refreshRetryTimerRef.current) {
          clearTimeout(refreshRetryTimerRef.current);
          refreshRetryTimerRef.current = null;
        }
        // La meta (precios/disponibilidad) se carga LAZY por propiedad vía
        // getPropertyCalendarMeta, FUERA del request principal — así el primer
        // paint del calendario NO espera el feed externo de Stays (antes era
        // ~90% de la latencia del cliente). Vale para admin (carga al expandir
        // una fila) y para cliente (carga la propiedad enfocada, ver el effect
        // en calendarioClienteMonth). El request principal ya no trae precios
        // (npData/scData vienen null); si por compat llegara meta inline, la
        // aplicamos igual. En refresh DURO reseteamos la cache para forzar la
        // re-carga lazy; en el suave (preserveMeta) conservamos lo ya cargado
        // para no colapsar lo expandido ni parpadear los precios.
        if (!opts.preserveMeta) {
          setNightPrices(npData ?? {});
          setStaysCalendarData(scData ?? {});
          setPropertyMetaCache(new Set());
        } else {
          if (npData) setNightPrices((prev) => ({ ...prev, ...npData }));
          if (scData) setStaysCalendarData((prev) => ({ ...prev, ...scData }));
        }
      } catch (e: any) {
        // Anti-carrera: si esta respuesta ya fue superada por un fetch más
        // nuevo, ignoramos su error — el reintento/estado lo maneja el vigente.
        if (mySeq !== fetchSeqRef.current) return;
        if (hasLoadedOnceRef.current) {
          // Refresh en background (WS de otro usuario, mutación propia,
          // reintento): NO destruir la vista actual. Antes este catch borraba
          // bookings/blocks/cleanings y seteaba el error fatal → si el GET
          // fallaba UNA vez (blip de red, redeploy del backend en Railway,
          // 500 transitorio) el panel que otro usuario estaba mirando pasaba
          // a "Error cargando calendario" con la grilla vacía y sin
          // reintento (bug visto 2026-07-03: cliente borra su bloqueo → el
          // broadcast dispara el refresh en el panel admin → el fetch falla
          // → pantalla de error). Ahora: se conserva la data en pantalla
          // (queda stale unos segundos), aviso discreto y reintento acotado.
          if (refreshRetryCountRef.current < MAX_REFRESH_RETRIES) {
            refreshRetryCountRef.current += 1;
            getMsg()?.warning({
              content: "No se pudo actualizar el calendario. Reintentando…",
              key: "cal-refresh-retry",
            });
            if (refreshRetryTimerRef.current) {
              clearTimeout(refreshRetryTimerRef.current);
            }
            refreshRetryTimerRef.current = setTimeout(() => {
              refreshRetryTimerRef.current = null;
              // Rango VIGENTE (no `r`): si el usuario movió el rango entre
              // el fallo y el reintento, no pisamos la vista con el viejo.
              fetchCalendarSelfRef.current?.(rangeRef.current, {
                preserveMeta: true,
              });
            }, REFRESH_RETRY_MS);
          } else {
            getMsg()?.error({
              content:
                "El calendario no se pudo actualizar. Usa “Refrescar” o recarga la página.",
              key: "cal-refresh-retry",
            });
          }
        } else {
          // Carga INICIAL fallida: no hay nada que conservar — pantalla de
          // error (el Alert ofrece "Reintentar", que llama a refetch()).
          setError(e?.message ?? "Error desconocido");
          setCleanings([]);
          setBlocks([]);
          setBookings([]);
          setNightPrices({});
          setStaysCalendarData({});
        }
      } finally {
        // Solo el fetch VIGENTE controla el velo de carga: un fetch superado
        // que termina no debe apagar loading/refreshing mientras el nuevo
        // sigue trayendo la data del rango actual.
        if (mySeq === fetchSeqRef.current) {
          setLoading(false);
          setRefreshing(false);
        }
        resolveInFlight();
        if (inFlightFetchRef.current?.promise === inFlightPromise) {
          inFlightFetchRef.current = null;
        }
      }
    },
    // Leemos bookings/blocks/cleanings sólo para detectar initial load — no
    // queremos reconstruir la función en cada llegada de data, así que los
    // omitimos de las deps (el valor leído es "stale" pero suficiente).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [focusedPropertyId, user?.id, user?.rol],
  );

  // Mantener el self-ref apuntando a la versión vigente (para el reintento).
  fetchCalendarSelfRef.current = fetchCalendar;

  // Limpiar el reintento pendiente al desmontar la página.
  useEffect(
    () => () => {
      if (refreshRetryTimerRef.current) {
        clearTimeout(refreshRetryTimerRef.current);
      }
    },
    [],
  );

  // Alias para mantener la API pública (usado por ClientBlockModal.onSubmit).
  const fetchAll = fetchCalendar;

  // Auto-seleccionar primera propiedad del cliente cuando llegan las
  // propiedades, para que fetchCalendar dispare sin delay del componente.
  // Este efecto SOLO debe correr para cliente — el admin ve todas las
  // propiedades simultáneamente y no requiere focus. `user.rol` escalar
  // (ver CLAUDE.md §3.3); isAdminRole normaliza string|string[]|null.
  const userIsAdminForEffect = isAdminRole(user?.rol);
  useEffect(() => {
    if (
      !userIsAdminForEffect &&
      focusedPropertyId == null &&
      properties.length > 0
    ) {
      // Restaurar la última propiedad que el cliente estaba viendo (persistida
      // en localStorage por calendarioClienteMonth) para que al RECARGAR la
      // página no vuelva a la propiedad por defecto (pedido del owner
      // 2026-07-03). Se hace acá, dentro del mismo auto-select, para evitar un
      // flash `properties[0]` → propiedad guardada. La validación contra
      // `properties` cubre el caso de otro cliente en el mismo navegador o una
      // propiedad que ya no le pertenece → cae a la primera.
      let restored: number | null = null;
      try {
        const saved = Number(
          localStorage.getItem("af.calendarioCliente.propiedadId"),
        );
        if (saved && properties.some((p) => p.id === saved)) {
          restored = saved;
        }
      } catch {
        /* localStorage no disponible (modo privado) → default */
      }
      setFocusedPropertyId(restored ?? properties[0].id);
    }
  }, [userIsAdminForEffect, focusedPropertyId, properties]);

  // Requests de meta EN VUELO (dedupe por propiedad+rango). Sin esto, cuando el
  // reset de `propertyMetaCache` (fetch duro / cambio de mes) re-dispara el
  // effect lazy del cliente MIENTRAS el primer fetch sigue en vuelo, se lanzaba
  // un SEGUNDO round-trip concurrente a la API externa de Stays para la misma
  // propiedad+rango. La clave incluye el rango para permitir re-pedir al navegar
  // de mes (una vez que el anterior resolvió).
  const inFlightMetaRef = useRef<Map<string, Promise<void>>>(new Map());

  /** Carga lazy de nightPrices + staysCalendarData para una propiedad (admin
   * al expandir fila; cliente para la propiedad enfocada). */
  const fetchPropertyMeta = useCallback(
    async (propertyId: number): Promise<void> => {
      if (propertyMetaCache.has(propertyId)) return; // ya cargado para el rango
      const key = `${propertyId}|${range.from}|${range.to}`;
      const inFlight = inFlightMetaRef.current.get(key);
      if (inFlight) return inFlight; // ya hay un request idéntico en vuelo
      const promise = (async () => {
        const data = await apiFetch<any>(
          `/stays/calendario/meta/${propertyId}?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
        );
        if (!data) return;
        if (data.nightPrices) {
          setNightPrices((prev) => ({ ...prev, [propertyId]: data.nightPrices }));
        }
        if (data.calendarData) {
          setStaysCalendarData((prev) => ({ ...prev, [propertyId]: data.calendarData }));
        }
        setPropertyMetaCache((prev) => new Set(prev).add(propertyId));
      })().finally(() => {
        inFlightMetaRef.current.delete(key);
      });
      inFlightMetaRef.current.set(key, promise);
      return promise;
    },
    [range.from, range.to, propertyMetaCache],
  );

  // Reset de la cache de meta al cambiar de RANGO (mes), INDEPENDIENTE del éxito
  // del fetch principal. Antes el reset vivía solo dentro del fetch DURO (rama
  // !preserveMeta); si ese fetch fallaba transitoriamente (redeploy Railway /
  // 500) y el reintento suave tenía éxito, la cache quedaba con el rango viejo
  // y el mes nuevo se quedaba sin precios (el gate `has(propertyId)` no es
  // range-aware). Este effect garantiza que al cambiar de mes se re-pida la meta
  // perezosa. El reset del fetch duro queda redundante pero inocuo.
  useEffect(() => {
    setPropertyMetaCache(new Set());
  }, [range.from, range.to]);

  /**
   * Refresca la meta de UNA propiedad de forma forzada (ignora la cache).
   * A diferencia de `fetchCalendar`, NO resetea `staysCalendarData` global
   * ni `bookings/blocks/cleanings`: solo reemplaza la entrada de esta
   * propiedad. Así, tras un cambio de precio en Stays, el "Base ($)" se
   * actualiza en su lugar sin remontar la grilla → no hay salto de scroll.
   */
  const refreshPropertyMeta = useCallback(
    async (propertyId: number) => {
      const data = await apiFetch<any>(
        `/stays/calendario/meta/${propertyId}?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`,
      );
      if (!data) return;
      if (data.nightPrices) {
        setNightPrices((prev) => ({ ...prev, [propertyId]: data.nightPrices }));
      }
      if (data.calendarData) {
        setStaysCalendarData((prev) => ({
          ...prev,
          [propertyId]: data.calendarData,
        }));
      }
      setPropertyMetaCache((prev) => new Set(prev).add(propertyId));
    },
    [range.from, range.to],
  );

  // Espejo en ref del set de propiedades con meta cargada (= las expandidas),
  // para poder leerlo dentro de refreshCalendar sin closures stale.
  const propertyMetaCacheRef = useRef(propertyMetaCache);
  useEffect(() => {
    propertyMetaCacheRef.current = propertyMetaCache;
  }, [propertyMetaCache]);

  /**
   * Refresh SUAVE del calendario: re-trae los eventos (arriendos / bloqueos /
   * aseos) SIN recargar la página, sin colapsar lo expandido y sin perder el
   * scroll. Con `includeMeta: true` (default) re-trae además los precios
   * (meta) de las propiedades alguna vez expandidas en la sesión/rango
   * (`propertyMetaCache` solo crece; se resetea al cambiar el rango). Todo
   * en paralelo para que sea rápido y fluido.
   *
   * `includeMeta: false` — para mutaciones de eventos y ticks WS: un cambio
   * de arriendo/bloqueo/aseo NO altera precios, y cada request de meta
   * golpea la API EXTERNA de Stays (round 2026-07-02: con N filas alguna vez
   * expandidas, una ráfaga de webhooks costaría 1+N requests por tick y N
   * llamadas upstream — amplificación innecesaria). Los cambios de precio
   * refrescan su meta por su propio path (refreshPropertyMeta en onSuccess).
   *
   * Diferencia con `refetch` (refresh duro): aquí NO se borra `staysCalendarData`
   * ni `propertyMetaCache` (preserveMeta), así las filas meta abiertas no
   * desaparecen ni un frame; sus precios se actualizan en su lugar.
   */
  const refreshCalendar = useCallback(
    async (opts: { includeMeta?: boolean } = {}) => {
      const { includeMeta = true } = opts;
      const ids = includeMeta ? Array.from(propertyMetaCacheRef.current) : [];
      await Promise.all([
        fetchCalendar(range, { preserveMeta: true }),
        ...ids.map((id) => refreshPropertyMeta(id)),
      ]);
    },
    [fetchCalendar, range, refreshPropertyMeta],
  );

  // Propiedades: una sola vez por usuario. Cambiar de propiedad (focus) NO
  // debería re-pedir esta lista.
  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  // Aseadoras: solo admin, una vez por sesión.
  useEffect(() => {
    if (isAdmin) fetchAseadoras();
  }, [isAdmin, fetchAseadoras]);

  // Calendario: cuando cambia rango o propiedad enfocada.
  //
  // Debounce corto (200 ms): al navegar meses MUY rápido (o cambiar de
  // propiedad seguido) el rango cambia varias veces por segundo. Sin esto se
  // dispara un fetch por cada estado intermedio — y para el cliente cada uno
  // hace round-trips a la API externa de Stays. Con el debounce solo se pide
  // el ÚLTIMO estado tras ~200 ms de quietud. El `range`/mes se actualiza al
  // instante (esto solo difiere la RED, no la UI). La CORRECCIÓN ya la
  // garantizan la guarda de secuencia + el dedupe con scope; esto es puramente
  // eficiencia. Los otros disparadores de fetch (WS, reintento, botón
  // Refrescar) llaman a fetchCalendar directo, sin pasar por acá, así que
  // siguen siendo inmediatos. (Round 2026-07-05.)
  useEffect(() => {
    const t = setTimeout(() => {
      fetchCalendar(range);
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, fetchCalendar]);

  // ─── Sync en tiempo real (WebSocket) ─────────────────────────────────────
  // Cuando cualquier usuario (admin/superadmin/cliente/aseador) crea, edita
  // o elimina un booking/bloqueo/mantención/aseo, el backend emite un
  // mensaje `CALENDAR_CHANGED` con la propiedad afectada. Aquí refrescamos
  // silenciosamente si esa propiedad está visible en el calendario actual.
  //
  // IMPORTANTE — preservar modales abiertos:
  //   El refetch solo actualiza `bookings / blocks / cleanings` del hook,
  //   NO toca el state del componente padre (`isCreateOpen`, `createRange`,
  //   `editInitialValues` en calendario.tsx), ni el `Form.useForm()` interno
  //   del modal. Por eso un admin que esté creando un evento NO pierde lo
  //   que escribió cuando llega un broadcast: el modal sigue montado con
  //   su form state intacto. NO agregar lógica aquí que cierre el modal ni
  //   resetee el form.
  //
  // Debounce: si el backend emite varios broadcasts en ráfaga (por ejemplo,
  // al borrar un booking y crear un bloqueo en la misma operación), los
  // coalescemos en un único refetch con delay de 400 ms.
  //
  // Refresh SUAVE (round 2026-07-02): antes este handler llamaba a
  // `fetchCalendar(range)` (duro). Por el closure stale de `isInitial`
  // (ver fetchCalendar), para admin eso SIEMPRE encendía `setLoading(true)`
  // → el early-return `if (loading)` de calendario.tsx desmontaba TODA la
  // grilla → se perdían las filas meta expandidas y el scroll volvía al
  // inicio. Como el broadcast también llega a la sesión que originó el
  // cambio (broadcastCalendarChanged no excluye al emisor), cualquier
  // mutación propia terminaba "recargando la página" 400 ms después del
  // refresh suave local. Regla del proyecto: toda modificación del
  // calendario se refresca EN SU LUGAR, sin colapsar lo expandido ni
  // mover el scroll → usamos `refreshCalendar` con `includeMeta: false`:
  // un CALENDAR_CHANGED señala cambio de EVENTOS, no de precios, y
  // re-traer metas aquí amplificaría requests contra la API de Stays en
  // ráfagas de webhooks (ver docstring de refreshCalendar).
  const { socket } = useWebSocket();
  const refetchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Última instancia de socket vista — para detectar RECONEXIONES. El
  // WebSocketContext crea un socket NUEVO en cada reconexión (y publica null
  // mientras está caído), así que `socket !== prev` con prev no-null ⇒ el WS
  // estuvo caído (típico: redeploy del backend) y pudimos perder broadcasts.
  const prevSocketRef = useRef<WebSocket | null>(null);
  useEffect(() => {
    if (!socket) return;
    const isReconnect =
      prevSocketRef.current != null && prevSocketRef.current !== socket;
    prevSocketRef.current = socket;
    if (isReconnect && hasLoadedOnceRef.current) {
      // Resync suave post-reconexión: mismo camino que un CALENDAR_CHANGED
      // (sin meta — los precios no cambian por eventos perdidos de este tipo,
      // y re-traer metas acá golpearía la API de Stays sin necesidad).
      refreshCalendar({ includeMeta: false });
    }
    const onMessage = (e: MessageEvent) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg?.type !== "CALENDAR_CHANGED") return;
        const pid = Number(msg.propertyId);
        // Solo refrescar si el evento afecta una propiedad visible para
        // este usuario (admin ve todas las de su lista; cliente ve las suyas).
        if (!properties?.some((p) => p.id === pid)) return;
        if (refetchDebounceRef.current) {
          clearTimeout(refetchDebounceRef.current);
        }
        refetchDebounceRef.current = setTimeout(() => {
          refetchDebounceRef.current = null;
          refreshCalendar({ includeMeta: false });
        }, 400);
      } catch {
        // mensaje no-JSON — ignorar
      }
    };
    socket.addEventListener("message", onMessage);
    return () => {
      socket.removeEventListener("message", onMessage);
      if (refetchDebounceRef.current) {
        clearTimeout(refetchDebounceRef.current);
        refetchDebounceRef.current = null;
      }
    };
  }, [socket, properties, refreshCalendar]);

  const itemsByProperty = useMemo(() => {
    const map = new Map<number, NormalizedItem[]>();

    // bookings
    for (const b of bookings) {
      const pid = b.idPropiedad ?? null;
      if (!pid || !b.ingreso || !b.salida) continue;
      const arr = map.get(pid) ?? [];
      arr.push({ kind: "booking", propertyId: pid, booking: b });
      map.set(pid, arr);
    }

    // cleanings
    for (const c of cleanings) {
      const pid = c.idPropiedad ?? null;
      const fecha = c.fecha ?? null;
      if (!pid || !fecha) continue;

      const interval = cleaningIntervalFromUrgencia(c);
      if (!interval) continue;

      const start = interval.start;
      const end = interval.end;

      const arr = map.get(pid) ?? [];
      arr.push({
        kind: "cleaning",
        propertyId: pid,
        cleaning: c,
        start,
        end,
      });
      map.set(pid, arr);
    }

    // blocks / maintenances
    for (const bm of blocks) {
      const pid = bm.idPropiedad ?? null;
      const fi = bm.fechaInicio ?? null;
      const ft = bm.fechaTermino ?? null;
      if (!pid || !fi || !ft) continue;

      const kind: "blocked" | "maintenance" =
        (bm.tipo as any) === "maintenance" ? "maintenance" : "blocked";

      const hmStart = parseTimeToHM(bm.horaInicio) ?? { h: 0, m: 0 };
      const hmEnd = parseTimeToHM(bm.horaTermino) ?? { h: 23, m: 59 };

      const start = toDateTime(fi, hmStart);
      const end = toDateTime(ft, hmEnd);

      const arr = map.get(pid) ?? [];
      arr.push({ kind, propertyId: pid, block: bm, start, end });
      map.set(pid, arr);
    }

    for (const [pid, arr] of Array.from(map.entries())) {
      arr.sort((a, b) => {
        const aLen =
          a.kind === "booking"
            ? (a.booking.noches ?? 0)
            : Math.abs(
                ((a as any).end?.getTime?.() ?? 0) -
                  ((a as any).start?.getTime?.() ?? 0),
              );
        const bLen =
          b.kind === "booking"
            ? (b.booking.noches ?? 0)
            : Math.abs(
                ((b as any).end?.getTime?.() ?? 0) -
                  ((b as any).start?.getTime?.() ?? 0),
              );

        if (aLen !== bLen) return bLen - aLen;

        const rank = (k: any) =>
          k === "blocked" || k === "maintenance" ? 0 : k === "booking" ? 1 : 2;

        const ra = rank(a.kind);
        const rb = rank(b.kind);
        if (ra !== rb) return ra - rb;

        return 0;
      });

      map.set(pid, arr);
    }

    return map;
  }, [bookings, cleanings, blocks]);

  // Mapa global bookingId → nombreHuesped para enriquecer aseos AUTO
  // con el huésped de su reserva padre, incluso si la reserva está en
  // otra propiedad (caso histórico del id_propiedad mismatch que se
  // arregló round 2026-05-30). Se reconstruye solo cuando cambian los
  // bookings — barato.
  const huespedByBookingId = useMemo(() => {
    const m = new Map<number, string>();
    for (const b of bookings) {
      if (b.id && b.nombreHuesped) m.set(b.id, b.nombreHuesped);
    }
    return m;
  }, [bookings]);

  // Mapa bookingId → ref COMPLETA del aseo AUTO vinculado (dirección inversa:
  // reserva → aseo). Alimenta la tarjeta de "Aseo vinculado" EDITABLE del modal
  // de detalle de la RESERVA. El aseo AUTO es 1:1 con el booking; tomamos el
  // primer cleaning cuyo `reservaId` apunte a la reserva. Solo incluye aseos
  // cargados en el rango visible.
  const cleaningRefByBookingId = useMemo(() => {
    const m = new Map<number, CalendarEventRef>();
    for (const c of cleanings) {
      if (c.reservaId == null || m.has(c.reservaId)) continue;
      const ref = buildCleaningEventRef(c, huespedByBookingId);
      if (ref) m.set(c.reservaId, ref);
    }
    return m;
  }, [cleanings, huespedByBookingId]);

  // Opciones de filtro = nombres únicos presentes en las propiedades cargadas
  // (ordenados alfabéticamente). Alimentan los selects de la barra.
  const propFilterOptions = useMemo(() => {
    const uniqSorted = (vals: (string | null | undefined)[]) =>
      Array.from(new Set(vals.filter((v): v is string => Boolean(v)))).sort((a, b) =>
        a.localeCompare(b, "es", { sensitivity: "base", numeric: true }),
      );
    return {
      region: uniqSorted(properties.map((p) => p.regionNombre)),
      comuna: uniqSorted(properties.map((p) => p.comunaNombre)),
      sector: uniqSorted(properties.map((p) => p.sectorNombre)),
      cliente: uniqSorted(properties.map((p) => p.clienteNombre)),
    };
  }, [properties]);

  // Los filtros guardan NOMBRES, no ids: un sector renombrado, un cliente que
  // cambió de nombre o una propiedad movida de comuna dejan un filtro que ya no
  // matchea nada, y la grilla queda vacía. Cuando llegan las propiedades, lo
  // que ya no existe se descarta. Solo escribe si hay algo que descartar: en el
  // uso normal los selects únicamente ofrecen valores que existen.
  useEffect(() => {
    if (properties.length === 0) return;
    const limpio = { ...propFilters };
    let sobra = false;
    for (const k of DIMENSIONES_FILTRO) {
      const validos = propFilters[k].filter((n) => propFilterOptions[k].includes(n));
      if (validos.length !== propFilters[k].length) {
        limpio[k] = validos;
        sobra = true;
      }
    }
    if (sobra) setPropFilters(limpio);
  }, [properties, propFilterOptions, propFilters, setPropFilters]);

  // Huéspedes (arrendatarios) por propiedad, según las reservas del rango
  // cargado. Alimenta el buscador de arrendatarios (filtra las propiedades que
  // tienen una reserva con ese huésped en el rango visible).
  const guestNamesByProperty = useMemo(() => {
    const m = new Map<number, string[]>();
    for (const b of bookings) {
      if (b.idPropiedad == null || !b.nombreHuesped) continue;
      const arr = m.get(b.idPropiedad) ?? [];
      arr.push(b.nombreHuesped);
      m.set(b.idPropiedad, arr);
    }
    return m;
  }, [bookings]);

  // Lista DERIVADA (filtrada + ordenada) que alimenta las filas. `properties`
  // queda intacto (lo usan otros consumidores: modal bulk, búsqueda por id, etc.).
  const viewProperties = useMemo(() => {
    const byName = (a: Property, b: Property) =>
      (a.nombre ?? "").localeCompare(b.nombre ?? "", "es", {
        sensitivity: "base",
        numeric: true,
      });
    const keyFor = (p: Property): string => {
      switch (propSort) {
        case "region":
          return p.regionNombre ?? "";
        case "comuna":
          return p.comunaNombre ?? "";
        case "sector":
          return p.sectorNombre ?? "";
        case "cliente":
          return p.clienteNombre ?? "";
        default:
          return "";
      }
    };
    const q = propSearch.trim();
    const gq = guestSearch.trim();
    const passesFilter = (p: Property) => {
      // Búsqueda libre (acento-insensible) por nombre de propiedad, cliente o
      // propietario.
      if (
        q &&
        !(
          fuzzyIncludes(p.nombre, q) ||
          fuzzyIncludes(p.clienteNombre, q) ||
          (p.propietarioNombres ?? []).some((n) => fuzzyIncludes(n, q))
        )
      ) {
        return false;
      }
      // Búsqueda por arrendatario (huésped): la propiedad debe tener una reserva
      // con un huésped que matchee, dentro del rango cargado.
      if (gq) {
        const guests = guestNamesByProperty.get(p.id) ?? [];
        if (!guests.some((n) => fuzzyIncludes(n, gq))) return false;
      }
      const f = propFilters;
      if (f.region.length && !f.region.includes(p.regionNombre ?? "")) return false;
      if (f.comuna.length && !f.comuna.includes(p.comunaNombre ?? "")) return false;
      if (f.sector.length && !f.sector.includes(p.sectorNombre ?? "")) return false;
      if (f.cliente.length && !f.cliente.includes(p.clienteNombre ?? "")) return false;
      return true;
    };
    return properties.filter(passesFilter).sort((a, b) => {
      if (propSort !== "nombre") {
        const ka = keyFor(a);
        const kb = keyFor(b);
        // Sin valor (ej. propiedad sin región) va al final.
        if (!ka && kb) return 1;
        if (ka && !kb) return -1;
        const c = ka.localeCompare(kb, "es", { sensitivity: "base", numeric: true });
        if (c !== 0) return c;
      }
      // Desempate (y orden por defecto) alfabético por nombre.
      return byName(a, b);
    });
  }, [properties, propSort, propFilters, propSearch, guestSearch, guestNamesByProperty]);

  const calendarRows: CalendarRow[] = useMemo(() => {
    const rows: CalendarRow[] = [];

    for (const property of viewProperties) {
      const items = itemsByProperty.get(property.id) ?? [];
      const tracks = buildTracksForProperty(
        property,
        items,
        dates,
        huespedByBookingId,
        cleaningRefByBookingId,
      );
      rows.push(...tracks);
    }

    return rows;
  }, [
    viewProperties,
    itemsByProperty,
    dates,
    huespedByBookingId,
    cleaningRefByBookingId,
  ]);

  const getCell = useCallback(
    (row: CalendarRow, date: string): CalendarCellData => {
      const cell = row.cellsByDate[date];
      if (cell) return cell;
      return defaultEmptyCell(row.propertyId, date, row.propertyName);
    },
    [],
  );

  // Útil para refrescar la grilla después de crear/editar/eliminar.
  const refetch = useCallback(() => fetchAll(range), [fetchAll, range]);

  async function fetchEventDetail(kind: string, id: number | string) {
    const json = await apiFetch<{ success: boolean; event: any; message?: string }>(
      `/stays/calendario/details?kind=${encodeURIComponent(kind)}&id=${encodeURIComponent(String(id))}`,
    );
    if (!json?.success) throw new Error(json?.message ?? "Respuesta inválida");
    return json.event ?? null;
  }

  return {
    loading,
    refreshing,
    error,
    blocks,
    range,
    setRange,
    setRangeSinRecordar,
    sidebarOpen,
    toggleSidebar,
    properties,
    propSearch,
    setPropSearch,
    setPropSearchSinRecordar,
    guestSearch,
    setGuestSearch,
    setGuestSearchSinRecordar,
    propSort,
    setPropSort,
    propFilters,
    setPropFilters,
    setPropFiltersSinRecordar,
    propFilterOptions,
    dates,
    nightPrices,
    staysCalendarData,
    fetchPropertyMeta,
    refreshPropertyMeta,
    refreshCalendar,
    focusedPropertyId,
    setFocusedPropertyId,
    getCell,
    calendarRows,
    filteredAseadoras,
    loadingAseadoras,
    fetchAseadoras,
    refetch,
    fetchEventDetail,
  };
}
